import { ma, NodeStateStatus, NodeType, SessionStatus } from '@lemonade-hq/chat-engine';
import type { FinishNodeState, RenderNodeState, SchemaData, SerializedHistory } from '@lemonade-hq/chat-engine';
import type { ChatEngineSession } from './chat_engine.client';
import type {
  ConversationMessage,
  SendMessageRequest,
  SessionMetadata,
  StartConversationResponse,
} from './conversation_router.client';

const extractUserContent = (message: ConversationMessage): string => {
  const payload = message.presentation?.payload as Record<string, unknown> | undefined;
  const presentationData = payload?.presentation as Record<string, unknown> | undefined;
  const frameData = presentationData?.frame as Record<string, unknown> | undefined;
  const title = frameData?.title as string;

  if (title) {
    return title;
  }

  if (typeof message.content === 'string') {
    return message.content;
  }

  return '';
};

export const convertMessagesToHistory = <TPresentation = undefined>(
  messages: ConversationMessage[],
): SerializedHistory<TPresentation, unknown> => {
  const historyItems: (FinishNodeState | RenderNodeState<TPresentation>)[] = [];

  for (let i = 0; i < messages.length; i++) {
    const message = messages[i];

    if (message.side !== 'assistant' || message.presentation?.type !== 'uniclient') {
      continue;
    }

    const payload = message.presentation.payload as Record<string, unknown> | undefined;
    const stepName = payload?.name as string;

    if ((payload?.type as string | undefined) === 'finish') {
      historyItems.push({
        id: message.id,
        name: stepName,
        type: NodeType.Finish,
        status: NodeStateStatus.Pending,
        payload: (payload?.payload as Record<string, unknown> | undefined) ?? {},
      } as FinishNodeState);
      continue;
    }

    const nextMessage = messages[i + 1];
    const isNextMessageByUser = Boolean(nextMessage) && nextMessage.side === 'user';
    const isResponseToClosedScript = isNextMessageByUser && nextMessage.generatedBy === 'script';
    const isResponseFreeText = isNextMessageByUser && nextMessage.generatedBy === 'user';

    const presentation = (payload?.presentation as Record<string, unknown> | undefined) ?? {};
    const data = (payload?.data as Record<string, unknown> | undefined) ?? {};
    const answers = {
      ...(isResponseToClosedScript ? (JSON.parse(extractUserContent(nextMessage)) as Record<string, unknown>) : {}),
      ...(payload?.answers as Record<string, unknown> | undefined),
    };

    const renderedStep = {
      name: stepName,
      presentation,
      data: { ...data, ...(isResponseFreeText ? { output: extractUserContent(nextMessage) } : {}) },
    };

    historyItems.push({
      id: message.id,
      name: stepName,
      renderedStep: renderedStep as unknown,
      status: NodeStateStatus.Pending,
      type: NodeType.Render,
      answers,
    } as RenderNodeState<TPresentation>);
  }

  return historyItems as unknown as SerializedHistory<TPresentation, unknown>;
};

export const fromStartConversationResponse = <TPresentation = undefined>(
  response: StartConversationResponse,
  experience: string,
): ChatEngineSession<TPresentation> => {
  const history = convertMessagesToHistory<TPresentation>(response.messages);
  const firstMessageSessionMetadata = (
    response.messages.find(
      msg =>
        msg.side === 'assistant' &&
        (msg.presentation?.payload as { sessionMetadata?: SessionMetadata } | undefined)?.sessionMetadata,
    )?.presentation?.payload as { sessionMetadata?: SessionMetadata } | undefined
  )?.sessionMetadata;

  const lastMessage = response.messages[response.messages.length - 1];
  const isFinished =
    Boolean(lastMessage) &&
    lastMessage.side === 'assistant' &&
    lastMessage.presentation?.type === 'uniclient' &&
    (lastMessage.presentation.payload as Record<string, unknown> | undefined)?.type === 'finish';

  return {
    schema: ma.record(firstMessageSessionMetadata?.schema ?? {}),
    assets: firstMessageSessionMetadata?.assets ?? [],
    auth: firstMessageSessionMetadata?.auth
      ? {
          userPublicId: firstMessageSessionMetadata.auth.userPublicId ?? undefined,
          visitorId: firstMessageSessionMetadata.auth.visitorId,
        }
      : {},
    script: {
      name: experience,
      version: '1',
      engineVersion: 'conversation-router',
      id: response.conversationId,
    },
    sessionId: response.conversationId,
    sessionStatus: isFinished ? SessionStatus.Finished : SessionStatus.Pending,
    history,
  };
};

export const convertAnswersToMessage = (
  answers: SchemaData,
  conversationId: string,
  replyToMessageId?: string,
): SendMessageRequest => {
  return {
    conversationId,
    input: answers,
    replyToMessageId,
  };
};

export const updateSessionWithNewMessages = <TPresentation = undefined>(
  session: ChatEngineSession<TPresentation>,
  allMessages: ConversationMessage[],
): ChatEngineSession<TPresentation> => {
  const existingMessageIds = new Set(session.history.map(h => h.id));
  const hasNewMessages = allMessages.some(msg => !existingMessageIds.has(msg.id));

  if (!hasNewMessages) {
    const newHistory = convertMessagesToHistory<TPresentation>(allMessages);

    if (newHistory.length !== session.history.length) {
      return {
        ...session,
        history: newHistory,
      };
    }

    const hasContentChanges = newHistory.some((newStep, index) => {
      const existingStep = session.history[index];
      if (existingStep.id !== newStep.id) {
        return true;
      }

      return JSON.stringify(newStep) !== JSON.stringify(existingStep);
    });

    if (!hasContentChanges) {
      return session;
    }

    return {
      ...session,
      history: newHistory,
    };
  }

  const completeHistory = convertMessagesToHistory<TPresentation>(allMessages);

  return {
    ...session,
    history: completeHistory,
  };
};
